import { Component } from '@theme/component';

/**
 * Documentation buttons of the product page (`blocks/pc-docs.liquid`).
 *
 * The Liquid `generic_file` object has no size, so the size of each file in KB is read here from the
 * `Content-Length` header of a `HEAD` request to the file (same origin as the storefront), with a one-byte `Range`
 * request as a fallback, and written next to the label. The links already work without it: when the request fails or
 * the header is missing, no size is shown.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement[]} [links] - Download links.
 * @property {HTMLElement[]} [sizes] - One size element per link, in the same order. Hidden until it has a value.
 *
 * @extends {Component<Refs>}
 */
class PcDocs extends Component {
  /** @type {AbortController | undefined} */
  #abort;

  connectedCallback() {
    super.connectedCallback();
    this.#load();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#load();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abort?.abort();
    this.#abort = undefined;
  }

  /**
   * Size of a file in bytes: the `Content-Length` of a `HEAD` request or, when a proxy drops it, the total of a
   * one-byte `Range` request (`Content-Range: bytes 0-0/<total>`).
   * @param {string} url
   * @param {AbortSignal} signal
   * @returns {Promise<number>} 0 when the size cannot be read.
   */
  async #fetchSize(url, signal) {
    const head = await fetch(url, { method: 'HEAD', signal });
    const length = Number(head.headers.get('content-length'));
    if (head.ok && Number.isFinite(length) && length > 0) return length;

    const range = await fetch(url, { headers: { Range: 'bytes=0-0' }, signal });
    const total = Number(range.headers.get('content-range')?.split('/')[1]);
    return range.ok && Number.isFinite(total) && total > 0 ? total : 0;
  }

  /**
   * Shows the size of every file that does not have it yet. Safe to call again after a re-render.
   */
  #load() {
    const { links = [], sizes = [] } = this.refs;
    const template = this.dataset.sizeTemplate ?? '';
    if (!template) return;

    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const format = new Intl.NumberFormat(document.documentElement.lang || undefined);

    links.forEach((link, index) => {
      const target = sizes[index];
      if (!target || !target.hidden) return;

      this.#fetchSize(link.href, signal)
        .then((bytes) => {
          if (!bytes) return;

          target.textContent = template.replace('{size}', format.format(Math.max(1, Math.round(bytes / 1024))));
          target.hidden = false;
        })
        .catch(() => {
          // Aborted or blocked: the button works without the size
        });
    });
  }
}

if (!customElements.get('pc-docs')) {
  customElements.define('pc-docs', PcDocs);
}
